import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  type EntityListOfStoryListItemDto,
  getStories,
  type SortDirection,
  type StoryState,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { MS_TIME } from "Utils/TimeUtils";

const STORIES_QUERY_KEY = "STORIES";

export type StoriesParams = {
  state?: StoryState;
  /** Author display-name search — honored for Admin only, other actors are scoped to themselves. */
  authorName?: string;
  text?: string;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
};

export const storiesKeys = {
  all: [STORIES_QUERY_KEY] as const,
  lists: () => [...storiesKeys.all, "list"] as const,
  list: (params?: StoriesParams) => [...storiesKeys.lists(), params] as const,
  details: () => [...storiesKeys.all, "detail"] as const,
  detail: (publicID?: string | null) =>
    [...storiesKeys.details(), publicID] as const,
};

/** The management list: one entry per story (drafts, live and expired), scoped server-side by role. */
export const useStoriesQuery = (params: StoriesParams = {}) =>
  useQuery<EntityListOfStoryListItemDto>({
    queryKey: storiesKeys.list(params),
    queryFn: processResponseWithApiProblemDetails(() =>
      getStories(
        params.state,
        params.authorName,
        params.text,
        params.offset,
        params.limit,
        params.sortBy,
        params.sortDirection,
      ),
    ),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: MS_TIME.minute,
  });
